import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Calendar as CalendarIcon,
  Filter,
  Download,
  Upload,
  FileText,
  Users,
  Euro,
  CheckCircle,
  Clock,
  Search,
  Edit,
  Trash2,
  X,
  Check,
  XCircle,
  AlertCircle
} from 'lucide-react';
import axios from 'axios';
import { usePermissions } from '../contexts/PermissionsContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.VITE_REACT_APP_BACKEND_URL;

const BillingOverview = () => {
  const { isAdminOrManager } = usePermissions();
  const [billings, setBillings] = useState([]);
  const [filteredBillings, setFilteredBillings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [teams, setTeams] = useState([]);
  
  // Detail modal state
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedBilling, setSelectedBilling] = useState(null);
  
  // Import dialog state
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    employeeId: '',
    teamId: '',
    status: 'all'
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [billings, filters]);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [billingsResp, employeesResp, teamsResp] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/billings`, config),
        axios.get(`${BACKEND_URL}/api/employees`, config),
        axios.get(`${BACKEND_URL}/api/teams`, config)
      ]);

      setBillings(billingsResp.data);
      setEmployees(employeesResp.data);
      setTeams(teamsResp.data);
    } catch (error) {
      console.error('Fehler beim Laden der Daten:', error);
      alert('Fehler beim Laden der Abrechnungen');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...billings];

    // Date filters
    if (filters.startDate) {
      filtered = filtered.filter(b => b.date >= filters.startDate);
    }
    if (filters.endDate) {
      filtered = filtered.filter(b => b.date <= filters.endDate);
    }

    // Employee filter
    if (filters.employeeId) {
      filtered = filtered.filter(b => b.employee_id === filters.employeeId);
    }

    // Team filter
    if (filters.teamId) {
      filtered = filtered.filter(b => b.team_id === filters.teamId);
    }

    // Status filter
    if (filters.status !== 'all') {
      filtered = filtered.filter(b => b.status === filters.status);
    }

    setFilteredBillings(filtered);
  };

  const resetFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      employeeId: '',
      teamId: '',
      status: 'all'
    });
  };

  const getEmployeeName = (employeeId) => {
    const employee = employees.find(e => e.id === employeeId);
    return employee ? `${employee.first_name} ${employee.last_name}` : 'Unbekannt';
  };

  const getTeamName = (teamNumber) => {
    const team = teams.find(t => t.team_number === teamNumber);
    return team ? `Team ${team.team_number}` : `Team ${teamNumber}`;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { 
        variant: 'outline', 
        icon: FileText, 
        label: 'Entwurf',
        color: 'text-slate-600'
      },
      submitted: { 
        variant: 'secondary', 
        icon: Clock, 
        label: 'Eingereicht',
        color: 'text-amber-600'
      },
      approved: { 
        variant: 'default', 
        icon: CheckCircle, 
        label: 'Genehmigt',
        color: 'text-green-600'
      },
      rejected: { 
        variant: 'destructive', 
        icon: XCircle, 
        label: 'Abgelehnt',
        color: 'text-red-600'
      }
    };
    
    return statusConfig[status] || statusConfig.draft;
  };

  const calculateTotalAmount = () => {
    return filteredBillings.reduce((sum, b) => sum + b.total_amount, 0);
  };

  const openBillingDetails = (billing) => {
    setSelectedBilling(billing);
    setShowDetailModal(true);
  };

  const deleteBilling = async (billingId) => {
    if (!window.confirm('Möchten Sie diese Abrechnung wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.')) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      await axios.delete(`${BACKEND_URL}/api/billings/${billingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert('Abrechnung erfolgreich gelöscht!');
      setShowDetailModal(false);
      loadData(); // Reload data
    } catch (error) {
      console.error('Fehler beim Löschen:', error);
      alert(error.response?.data?.detail || 'Fehler beim Löschen der Abrechnung');
    }
  };

  const editBilling = (billing) => {
    // Close detail modal and open edit modal
    setShowDetailModal(false);
    // TODO: Implement edit functionality
    alert('Bearbeiten-Funktion: Diese würde ein Bearbeitungs-Modal öffnen. Für jetzt können Sie die Abrechnung im Kalender des Mitarbeiters bearbeiten.');
  };

  const approveBilling = async (billing) => {
    if (!window.confirm('Möchten Sie diese Abrechnung genehmigen?')) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      await axios.post(`${BACKEND_URL}/api/billings/${billing.id}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert('Abrechnung erfolgreich genehmigt!');
      setShowDetailModal(false);
      loadData();
    } catch (error) {
      console.error('Fehler beim Genehmigen:', error);
      alert(error.response?.data?.detail || 'Fehler beim Genehmigen der Abrechnung');
    }
  };

  const rejectBilling = async (billing) => {
    const reason = prompt('Bitte geben Sie den Grund für die Ablehnung ein:');
    
    if (!reason || reason.trim().length === 0) {
      alert('Ablehnungsgrund ist erforderlich');
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      await axios.post(
        `${BACKEND_URL}/api/billings/${billing.id}/reject?rejection_reason=${encodeURIComponent(reason)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert('Abrechnung wurde abgelehnt!');
      setShowDetailModal(false);
      loadData();
    } catch (error) {
      console.error('Fehler beim Ablehnen:', error);
      alert(error.response?.data?.detail || 'Fehler beim Ablehnen der Abrechnung');
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${BACKEND_URL}/api/billings/export/excel`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `abrechnungen_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      alert('Export erfolgreich!');
    } catch (error) {
      console.error('Fehler beim Export:', error);
      alert('Fehler beim Export');
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      alert('Bitte wählen Sie eine Datei aus');
      return;
    }
    
    setImporting(true);
    try {
      const token = localStorage.getItem('authToken');
      const formData = new FormData();
      formData.append('file', importFile);
      
      const response = await axios.post(
        `${BACKEND_URL}/api/billings/import/excel`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      alert(`Import erfolgreich!\nNeu importiert: ${response.data.imported}${response.data.errors.length > 0 ? '\n\nFehler:\n' + response.data.errors.join('\n') : ''}`);
      setShowImportDialog(false);
      setImportFile(null);
      loadData();
    } catch (error) {
      console.error('Fehler beim Import:', error);
      alert(error.response?.data?.detail || 'Fehler beim Import');
    } finally {
      setImporting(false);
    }
  };

  if (!isAdminOrManager()) {
    return (
      <div className="min-h-full bg-slate-50 p-6 flex items-center justify-center">
        <Card>
          <CardContent className="pt-6">
            <p className="text-slate-600">Keine Berechtigung zum Anzeigen dieser Seite.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-slate-50 p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
              <Euro className="w-8 h-8 text-emerald-600" />
              Abrechnungsübersicht
            </h1>
            <p className="text-slate-600 mt-1">
              Alle Abrechnungen von Technikern im Überblick
            </p>
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleExport}>
              <Download className="w-4 h-4 mr-2" />
              Excel exportieren
            </Button>
            <Button variant="outline" onClick={() => setShowImportDialog(true)}>
              <Upload className="w-4 h-4 mr-2" />
              Excel importieren
            </Button>
            <Button onClick={loadData}>
              <Search className="w-4 h-4 mr-2" />
              Aktualisieren
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Gesamt</p>
                  <p className="text-2xl font-bold text-slate-900">{filteredBillings.length}</p>
                </div>
                <FileText className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Eingereicht</p>
                  <p className="text-2xl font-bold text-amber-600">
                    {filteredBillings.filter(b => b.status === 'submitted').length}
                  </p>
                </div>
                <Clock className="w-8 h-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Genehmigt</p>
                  <p className="text-2xl font-bold text-green-600">
                    {filteredBillings.filter(b => b.status === 'approved').length}
                  </p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Abgelehnt</p>
                  <p className="text-2xl font-bold text-red-600">
                    {filteredBillings.filter(b => b.status === 'rejected').length}
                  </p>
                </div>
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Gesamtsumme</p>
                  <p className="text-2xl font-bold text-emerald-600">
                    {calculateTotalAmount().toFixed(2)} €
                  </p>
                </div>
                <Euro className="w-8 h-8 text-emerald-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-emerald-600" />
            Filter
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Von Datum</label>
              <Input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Bis Datum</label>
              <Input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Mitarbeiter</label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={filters.employeeId}
                onChange={(e) => setFilters({ ...filters, employeeId: e.target.value })}
              >
                <option value="">Alle Mitarbeiter</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>
                    {emp.first_name} {emp.last_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Status</label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <option value="all">Alle Status</option>
                <option value="draft">Entwurf</option>
                <option value="submitted">Eingereicht</option>
                <option value="approved">Genehmigt</option>
                <option value="rejected">Abgelehnt</option>
              </select>
            </div>

            <div className="flex items-end">
              <Button variant="outline" onClick={resetFilters} className="w-full">
                Filter zurücksetzen
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Billings List */}
      <Card>
        <CardHeader>
          <CardTitle>Abrechnungen ({filteredBillings.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12">
              <p className="text-slate-500">Lade Abrechnungen...</p>
            </div>
          ) : filteredBillings.length === 0 ? (
            <div className="text-center py-12 bg-slate-50 rounded-lg">
              <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Keine Abrechnungen gefunden</p>
              <p className="text-slate-400 text-sm mt-1">
                Passen Sie die Filter an oder fügen Sie neue Abrechnungen hinzu
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredBillings.map((billing) => {
                const statusConfig = getStatusBadge(billing.status);
                const StatusIcon = statusConfig.icon;
                
                return (
                  <Card key={billing.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <Badge variant={statusConfig.variant}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {statusConfig.label}
                            </Badge>
                            <span className="text-lg font-semibold">
                              {new Date(billing.date).toLocaleDateString('de-DE', {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </span>
                          </div>

                          <div className="grid grid-cols-4 gap-4 text-sm text-slate-600">
                            <div className="flex items-center gap-2">
                              <Users className="w-4 h-4" />
                              <span>{getEmployeeName(billing.employee_id)}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <CalendarIcon className="w-4 h-4" />
                              <span>{getTeamName(billing.team_number)}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <FileText className="w-4 h-4" />
                              <span>{billing.orders.length} Auftrag(e)</span>
                            </div>
                            <div className="flex items-center gap-2 font-semibold text-emerald-600">
                              <Euro className="w-4 h-4" />
                              <span>{billing.total_amount.toFixed(2)} €</span>
                            </div>
                          </div>

                          {billing.support_teams && billing.support_teams.length > 0 && (
                            <div className="mt-2 text-sm text-slate-500">
                              Unterstützt von: {billing.support_teams.map(t => `Team ${t}`).join(', ')}
                            </div>
                          )}

                          {billing.status === 'approved' && billing.approved_at && (
                            <div className="mt-2 text-xs text-slate-400">
                              Genehmigt am: {new Date(billing.approved_at).toLocaleString('de-DE')}
                            </div>
                          )}

                          {billing.status === 'submitted' && billing.submitted_at && (
                            <div className="mt-2 text-xs text-slate-400">
                              Eingereicht am: {new Date(billing.submitted_at).toLocaleString('de-DE')}
                            </div>
                          )}

                          {billing.status === 'rejected' && billing.rejected_at && (
                            <div className="mt-2 text-xs text-red-600">
                              Abgelehnt am: {new Date(billing.rejected_at).toLocaleString('de-DE')}
                              {billing.rejection_reason && (
                                <span className="block mt-1 font-medium">Grund: {billing.rejection_reason}</span>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openBillingDetails(billing)}
                          >
                            Details
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Billing Detail Modal */}
      {showDetailModal && selectedBilling && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b sticky top-0 bg-white z-10">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">
                    Abrechnungs-Details
                  </h2>
                  <p className="text-slate-600 mt-1">
                    {new Date(selectedBilling.date).toLocaleDateString('de-DE', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </div>
                <div className="flex gap-2">
                  {isAdminOrManager() && (
                    <>
                      <Button 
                        variant="outline" 
                        onClick={() => editBilling(selectedBilling)}
                        className="border-blue-500 text-blue-600 hover:bg-blue-50"
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Bearbeiten
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={() => deleteBilling(selectedBilling.id)}
                        className="border-red-500 text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Löschen
                      </Button>
                    </>
                  )}
                  <Button variant="outline" onClick={() => setShowDetailModal(false)}>
                    <X className="w-4 h-4 mr-2" />
                    Schließen
                  </Button>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Status Badge */}
              <div className="flex items-center gap-3">
                {(() => {
                  const statusConfig = getStatusBadge(selectedBilling.status);
                  const StatusIcon = statusConfig.icon;
                  return (
                    <Badge variant={statusConfig.variant} className="text-base py-2 px-4">
                      <StatusIcon className="w-4 h-4 mr-2" />
                      {statusConfig.label}
                    </Badge>
                  );
                })()}
                
                {selectedBilling.status === 'approved' && selectedBilling.approved_at && (
                  <span className="text-sm text-slate-500">
                    Genehmigt am: {new Date(selectedBilling.approved_at).toLocaleString('de-DE')}
                  </span>
                )}
                {selectedBilling.status === 'submitted' && selectedBilling.submitted_at && (
                  <span className="text-sm text-slate-500">
                    Eingereicht am: {new Date(selectedBilling.submitted_at).toLocaleString('de-DE')}
                  </span>
                )}
                {selectedBilling.status === 'rejected' && selectedBilling.rejected_at && (
                  <span className="text-sm text-red-500">
                    Abgelehnt am: {new Date(selectedBilling.rejected_at).toLocaleString('de-DE')}
                  </span>
                )}
              </div>

              {/* Rejection Reason - if rejected */}
              {selectedBilling.status === 'rejected' && selectedBilling.rejection_reason && (
                <Card className="bg-red-50 border-red-200">
                  <CardContent className="pt-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                      <div>
                        <p className="font-semibold text-red-900 mb-1">Ablehnungsgrund:</p>
                        <p className="text-red-800">{selectedBilling.rejection_reason}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Workflow Buttons - for Admin/Manager and status=submitted */}
              {isAdminOrManager() && selectedBilling.status === 'submitted' && (
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-blue-900 mb-1">Abrechnung prüfen</p>
                        <p className="text-sm text-blue-700">
                          Diese Abrechnung wurde zur Prüfung eingereicht und wartet auf Ihre Genehmigung.
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => approveBilling(selectedBilling)}
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          <Check className="w-4 h-4 mr-2" />
                          Genehmigen
                        </Button>
                        <Button
                          onClick={() => rejectBilling(selectedBilling)}
                          variant="outline"
                          className="border-red-500 text-red-600 hover:bg-red-50"
                        >
                          <X className="w-4 h-4 mr-2" />
                          Ablehnen
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Employee & Team Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-500" />
                    Mitarbeiter & Team
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-600">Mitarbeiter</p>
                      <p className="font-semibold">{getEmployeeName(selectedBilling.employee_id)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-600">Team</p>
                      <p className="font-semibold">{getTeamName(selectedBilling.team_number)}</p>
                    </div>
                  </div>
                  
                  {selectedBilling.support_teams && selectedBilling.support_teams.length > 0 && (
                    <div className="mt-4 pt-4 border-t">
                      <p className="text-sm text-slate-600 mb-2">Unterstützt von:</p>
                      <div className="flex flex-wrap gap-2">
                        {selectedBilling.support_teams.map(teamNum => (
                          <Badge key={teamNum} variant="outline">
                            Team {teamNum}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Orders */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-emerald-500" />
                    Aufträge ({selectedBilling.orders?.length || 0})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {!selectedBilling.orders || selectedBilling.orders.length === 0 ? (
                    <p className="text-slate-500 text-center py-4">Keine Aufträge erfasst</p>
                  ) : (
                    selectedBilling.orders.map((order, index) => (
                      <Card key={index} className="border-l-4 border-l-blue-500">
                        <CardContent className="pt-4">
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <h3 className="font-semibold text-lg">
                                {order.order_type === 'gwv' ? 'GWV' : 'Connect'} {order.order_number}
                              </h3>
                              <p className="text-sm text-slate-600">
                                SMILE: <span className="font-mono">{order.smile_number || 'Nicht angegeben'}</span>
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-slate-600">Auftragssumme</p>
                              <p className="text-xl font-bold text-emerald-600">
                                {order.total?.toFixed(2) || '0.00'} €
                              </p>
                            </div>
                          </div>

                          {/* Positions */}
                          {order.positions && order.positions.length > 0 && (
                            <div className="mt-3 space-y-2">
                              <p className="text-sm font-medium text-slate-700">Positionen:</p>
                              {order.positions.map((position, posIndex) => (
                                <div key={posIndex} className="bg-slate-50 p-3 rounded flex items-center justify-between">
                                  <div className="flex-1">
                                    <p className="font-medium text-sm">
                                      {position.position_number} - {position.description}
                                    </p>
                                    <p className="text-xs text-slate-500">
                                      {position.unit_price?.toFixed(2) || '0.00'} € / {position.unit}
                                    </p>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-sm text-slate-600">
                                      Menge: {position.quantity}
                                    </p>
                                    <p className="font-semibold">
                                      {position.subtotal?.toFixed(2) || '0.00'} €
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))
                  )}
                </CardContent>
              </Card>

              {/* Total Amount */}
              <Card className="bg-emerald-50 border-emerald-200">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-slate-600 mb-1">Gesamtsumme</p>
                      <p className="text-3xl font-bold text-emerald-700">
                        {selectedBilling.total_amount?.toFixed(2) || '0.00'} €
                      </p>
                    </div>
                    <Euro className="w-16 h-16 text-emerald-300" />
                  </div>
                </CardContent>
              </Card>

              {/* Metadata */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Metadaten</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {selectedBilling.created_at && (
                      <div>
                        <p className="text-slate-600">Erstellt am</p>
                        <p className="font-medium">
                          {new Date(selectedBilling.created_at).toLocaleString('de-DE')}
                        </p>
                      </div>
                    )}
                    {selectedBilling.updated_at && (
                      <div>
                        <p className="text-slate-600">Aktualisiert am</p>
                        <p className="font-medium">
                          {new Date(selectedBilling.updated_at).toLocaleString('de-DE')}
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      )}

      {/* Import Dialog */}
      {showImportDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Excel Import</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Excel-Datei auswählen</label>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => setImportFile(e.target.files[0])}
                  className="w-full p-2 border border-gray-300 rounded"
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowImportDialog(false);
                    setImportFile(null);
                  }}
                >
                  Abbrechen
                </Button>
                <Button
                  onClick={handleImport}
                  disabled={!importFile || importing}
                  className="gap-2"
                >
                  {importing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Importiere...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Importieren
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingOverview;
